import React, { useState, useMemo } from 'react';
import { TrendingUp, MapPin, Calendar, Clock, Filter, Download } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import KPICard from './KPICard';
import VolumeChart from './VolumeChart';
import ProfitChart from './ProfitChart';
import SegmentPerformance from './SegmentPerformance';
import CriticalAlerts from './CriticalAlerts';
import CafePerformance from './CafePerformance';
import DetailedSegmentAnalysis from './DetailedSegmentAnalysis';
import TradeAreaIntelligence from './TradeAreaIntelligence';

const performanceData = [
  { month: 'Jan', retailVolume: 2950, margin: 0.88, target: 3100 },
  { month: 'Feb', retailVolume: 2900, margin: 0.86, target: 3100 },
  { month: 'Mar', retailVolume: 2850, margin: 0.85, target: 3100 },
  { month: 'Apr', retailVolume: 2800, margin: 0.84, target: 3100 },
  { month: 'May', retailVolume: 2750, margin: 0.83, target: 3100 },
  { month: 'Jun', retailVolume: 2700, margin: 0.82, target: 3100 },
  { month: 'Jul', retailVolume: 2650, margin: 0.81, target: 3100 },
  { month: 'Aug', retailVolume: 2612, margin: 0.80, target: 3100 },
  { month: 'Sep', retailVolume: 2612, margin: 0.51, target: 3100 },
  { month: 'Oct', retailVolume: 2700, margin: 0.75, target: 3100 },
  { month: 'Nov', retailVolume: 2800, margin: 0.80, target: 3100 },
  { month: 'Dec', retailVolume: 2849, margin: 0.83, target: 3100 },
];

const segmentData = [
  { name: 'Urban Premium', stations: 523, performance: 97, trend: 'up' },
  { name: 'Highway Transit', stations: 682, performance: 88, trend: 'down' },
  { name: 'Rural Standard', stations: 754, performance: 92, trend: 'up' },
  { name: 'Suburban Mixed', stations: 384, performance: 84, trend: 'down' },
];

const alertsData = [
  { id: 1, type: 'competitive', message: 'Competitor price drops detected in 28 trade areas', severity: 'high', timestamp: '2 hours ago' },
  { id: 2, type: 'performance', message: 'Volume decline >10% in Highway Transit segment', severity: 'high', timestamp: '6 hours ago' },
  { id: 3, type: 'opportunity', message: 'Cross-sell potential identified in Urban Premium segment', severity: 'medium', timestamp: '1 day ago' },
  { id: 4, type: 'system', message: 'Data refresh complete for all 2,343 stations', severity: 'low', timestamp: '1 day ago' },
  { id: 5, type: 'operational', message: 'Maintenance needed at 12 stations in Rural segment', severity: 'medium', timestamp: '3 hours ago' },
  { id: 6, type: 'financial', message: 'Q3 profit forecast exceeds target by 5.2%', severity: 'low', timestamp: '1 day ago' },
];

const cafeAmazonData = [
  { month: 'Jan', cups: 32.5 },
  { month: 'Feb', cups: 33.0 },
  { month: 'Mar', cups: 33.2 },
  { month: 'Apr', cups: 33.5 },
  { month: 'May', cups: 33.8 },
  { month: 'Jun', cups: 34.0 },
  { month: 'Jul', cups: 33.2 },
  { month: 'Aug', cups: 33.5 },
  { month: 'Sep', cups: 33.0 },
  { month: 'Oct', cups: 34.0 },
  { month: 'Nov', cups: 34.5 },
  { month: 'Dec', cups: 35.2 },
];

const revenueData = [
  { month: 'Jan', value: 1250 },
  { month: 'Feb', value: 1280 },
  { month: 'Mar', value: 1310 },
  { month: 'Apr', value: 1350 },
  { month: 'May', value: 1370 },
  { month: 'Jun', value: 1390 },
  { month: 'Jul', value: 1410 },
  { month: 'Aug', value: 1430 },
  { month: 'Sep', value: 1450 },
  { month: 'Oct', value: 1470 },
  { month: 'Nov', value: 1500 },
  { month: 'Dec', value: 1530 },
];

const ExecutiveDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [comparisonType, setComparisonType] = useState('target');
  const [volumeTimePeriod, setVolumeTimePeriod] = useState('monthly');
  const [volumeComparisonType, setVolumeComparisonType] = useState('target');
  const [volumeFuelType, setVolumeFuelType] = useState('all');
  const [profitTimePeriod, setProfitTimePeriod] = useState('monthly');
  const [profitComparisonType, setProfitComparisonType] = useState('target');
  const [revenueTimePeriod, setRevenueTimePeriod] = useState('monthly');
  const [revenueComparisonType, setRevenueComparisonType] = useState('target');
  
  const processedRevenueData = useMemo(() => {
    let filteredData = [...revenueData];
    
    switch(revenueTimePeriod) {
      case 'daily':
        filteredData = revenueData.slice(-7);
        break;
      case 'weekly':
        filteredData = revenueData.filter((_, index) => index % 2 === 0);
        break;
      case 'quarterly':
        filteredData = revenueData.filter((_, index) => index % 3 === 0);
        break;
      case 'ytd':
        filteredData = revenueData.slice(0);
        break;
      case 'monthly':
      default:
        filteredData = revenueData;
        break;
    }
    
    switch(revenueComparisonType) {
      case 'previous':
        return filteredData.map((item, index, array) => ({
          ...item,
          margin: item.value / 1000,
          previousValue: index > 0 ? array[index-1].value / 1000 : null,
          comparisonLabel: 'Previous Period'
        }));
      case 'lastyear':
        return filteredData.map(item => ({
          ...item,
          margin: item.value / 1000,
          previousValue: (item.value * 0.9) / 1000,
          comparisonLabel: 'Last Year'
        }));
      case 'target':
      default:
        return filteredData.map(item => ({
          ...item,
          margin: item.value / 1000,
          targetValue: 1.5,
          comparisonLabel: 'Target'
        }));
    }
  }, [revenueData, revenueTimePeriod, revenueComparisonType]);

  const colors = {
    primary: '#A100FF',
    secondary: '#5E0060',
    tertiary: '#D6ABFF',
    alert: '#FF3B30',
    warning: '#FFCC00',
    success: '#34C759',
    background: '#F8F8F8',
    darkGrey: '#424242',
    mediumGrey: '#9E9E9E',
    lightGrey: '#E0E0E0',
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <DashboardHeader activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="flex-1 p-6">
        {activeTab === 'overview' && (
          <>
            <div className="flex justify-between mb-6">
              <div className="flex items-center">
                <button className="flex items-center bg-purple-100 text-purple-800 px-3 py-1 rounded-md text-sm">
                  <Calendar size={14} className="mr-1" />
                  <span>Last Updated: Today, 10:45 AM</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-6 mb-6">
              <KPICard 
                title="Total Volume (MML)" 
                value="7,075" 
                icon={TrendingUp} 
                positiveMetric="+9.3%" 
                positiveLabel="vs Last Quarter" 
                negativeMetric="-4.4%" 
                negativeLabel="vs Last Year"
                alertThreshold={-3.0}
              />

              <KPICard 
                title="Overall Revenue" 
                value="฿1.53B" 
                icon={TrendingUp} 
                positiveMetric="+5.1%" 
                positiveLabel="vs Last Quarter" 
                negativeMetric="+8.4%" 
                negativeLabel="vs Last Year"
              />

              <KPICard 
                title="Avg Trnx Value" 
                value="356 ฿" 
                icon={TrendingUp} 
                positiveMetric="+12%" 
                positiveLabel="vs Last Quarter" 
                negativeMetric="+8.7%" 
                negativeLabel="vs Last Year"
              />

              <KPICard 
                title="Stations Network" 
                value="2,343" 
                icon={MapPin} 
                positiveMetric="+64" 
                positiveLabel="vs Last Quarter" 
                negativeMetric="+87" 
                negativeLabel="vs Last Year"
              />
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <VolumeChart 
                data={performanceData} 
                title="Retail Volume Trend (MML)" 
                timePeriod={volumeTimePeriod}
                setTimePeriod={setVolumeTimePeriod}
                comparisonType={volumeComparisonType}
                setComparisonType={setVolumeComparisonType}
                fuelType={volumeFuelType}
                setFuelType={setVolumeFuelType}
              />
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Revenue Trend (฿B)</h3>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      <Filter size={14} className="text-gray-500" />
                      <select 
                        className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
                        value={revenueTimePeriod}
                        onChange={(e) => setRevenueTimePeriod(e.target.value)}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="ytd">Year-to-Date</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Compare:</span>
                      <select 
                        className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
                        value={revenueComparisonType}
                        onChange={(e) => setRevenueComparisonType(e.target.value)}
                      >
                        <option value="previous">Previous Period</option>
                        <option value="lastyear">Same Period Last Year</option>
                        <option value="target">Target</option>
                      </select>
                    </div>
                    <button className="p-1 rounded-full hover:bg-gray-100">
                      <Download size={16} className="text-gray-600" />
                    </button>
                  </div>
                </div>
                <div className="h-64">
                  <div className="h-full">
                    <ProfitChart 
                      data={processedRevenueData} 
                      title="" 
                      hideTitle={true}
                    />
                  </div>
                </div>
                
                <div className="mt-2 flex space-x-4">
                  <button className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">1W</button>
                  <button className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">1M</button>
                  <button className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">3M</button>
                  <button className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">6M</button>
                  <button className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">1Y</button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <SegmentPerformance segments={segmentData} />
              <CriticalAlerts alerts={alertsData} />
              <CafePerformance data={cafeAmazonData} />
            </div>
          </>
        )}
        
        {activeTab === 'segments' && (
          <DetailedSegmentAnalysis />
        )}
        
        {activeTab === 'trade' && (
          <TradeAreaIntelligence />
        )}
        
        {activeTab === 'campaigns' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-4">Campaign Management</h2>
            <p className="text-gray-600">Campaign management content will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
